import { useMemo, useRef } from 'react';
import { useEmployeeSuggestionsService } from 'domain/shared/employee';
import {
  type FilterBarFilterOption,
  type FilterBarFiltersProps,
  type FilterBarSearchInputProps,
  type FilterState,
} from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import { VENDOR_PROFILE_STATUSES_VALUES, VENDOR_WORKING_STATUSES_VALUES } from '../constants';

interface UseVendorsFilterBarParams {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  resetFilters: () => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
}

type EmployeeSuggestion = { alias: string; nameEn?: { firstName: string; lastName: string } };

const BOOLEAN_OPTIONS: FilterBarFilterOption[] = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
];

const toOptions = (labels: Record<string, string>): FilterBarFilterOption[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const mapEmployeeOption = (employee: EmployeeSuggestion): FilterBarFilterOption => ({
  value: employee.alias,
  label: [employee.alias, employee.nameEn?.firstName, employee.nameEn?.lastName].filter(Boolean).join(' '),
});

const useVendorsFilterBar = ({
  filters,
  setFilters,
  resetFilters,
  searchValue,
  setSearchValue,
}: UseVendorsFilterBarParams): {
  searchProps: FilterBarSearchInputProps;
  filterProps: FilterBarFiltersProps;
} => {
  const { searchWithContractors } = useEmployeeSuggestionsService();

  const searchEmployeesRef = useRef(searchWithContractors);
  searchEmployeesRef.current = searchWithContractors;

  const ownerOptions = useMemo(
    () => ({
      loadOptions: async (search: string) => {
        const response = await searchEmployeesRef.current({ filter: { searchString: { ct: search } } });
        return (response.result ?? []).map(mapEmployeeOption);
      },
      resolveOption: async (alias: string) => {
        const response = await searchEmployeesRef.current({ filter: { alias: { eq: alias } } });
        const employee = response.result?.[0];
        return employee ? mapEmployeeOption(employee) : null;
      },
    }),
    [],
  );

  return {
    searchProps: {
      label: 'Search',
      placeholder: 'Search',
      value: searchValue,
      onChange: setSearchValue,
    },
    filterProps: {
      value: filters,
      onChange: setFilters,
      onReset: resetFilters,
      filters: [
        {
          id: 'owner',
          source: 'owner',
          label: 'Owner',
          type: 'autosuggestion',
          condition: FilterCondition.EQ,
          minSearchLength: 1,
          ...ownerOptions,
        },
        {
          id: 'profileStatus',
          source: 'profileStatus',
          label: 'Profile status',
          type: 'dropdown',
          condition: FilterCondition.IN,
          multiple: true,
          options: toOptions(VENDOR_PROFILE_STATUSES_VALUES),
        },
        {
          id: 'workingStatus',
          source: 'workingStatus',
          label: 'Working status',
          type: 'dropdown',
          condition: FilterCondition.IN,
          multiple: true,
          options: toOptions(VENDOR_WORKING_STATUSES_VALUES),
        },
        {
          id: 'isCompliant',
          source: 'isCompliant',
          label: 'Compliant',
          type: 'dropdown',
          condition: FilterCondition.EQ,
          multiple: false,
          options: BOOLEAN_OPTIONS,
        },
      ],
    },
  };
};

export default useVendorsFilterBar;
